// The Upload CSV dialog of User Management: choose a CSV file of users, validate it (every row is
// checked, nothing saved), then create them all at once; each new user gets the "set your password"
// e-mail. Built like the Upload CSV tab of Device Management's Import Devices dialog.
import React, { useEffect, useState } from "react";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import Button from "@mui/material/Button";
import Table from "@mui/material/Table";
import TableHead from "@mui/material/TableHead";
import TableBody from "@mui/material/TableBody";
import TableRow from "@mui/material/TableRow";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import Alert from "@mui/material/Alert";
import LinearProgress from "@mui/material/LinearProgress";
import { useImportUsersCsvMutation } from "services/user";

// the server takes at most 200 rows in a 1 MB request
const CSV_MAX_BYTES = 500 * 1024;
const CSV_TEMPLATE = "email,firstName,lastName,role,userName,phone,tfa\r\n";
// the CSV file's columns, as the dialog explains them: [column, needed, what goes in it, example]
const csvGuide = (tfaAllowed) => [
  ["email", "Yes", "The person's e-mail address, not already used in your organisation; the \"set your password\" e-mail goes here", "ana.lee@example.com"],
  ["firstName", "Yes", "First name", "Ana"],
  ["lastName", "Yes", "Last name", "Lee"],
  ["role", "Yes", "Role name or id; one you are allowed to assign; rows can name different roles", "Site Viewer"],
  ["userName", "No", "Sign-in name, no spaces, not already taken; empty uses the e-mail", "ana.lee"],
  ["phone", "No", "9 to 15 characters of digits, spaces and +", "+44 7700 900123"],
  [
    "tfa",
    "No",
    tfaAllowed
      ? "Enforce two-factor sign-in: yes or no; empty means no"
      : "Leave empty or no: your licence does not let administrators enforce two-factor sign-in",
    "no",
  ],
];

export default function ImportUsers({ onClose, tfaAllowed }) {
  const [file, setFile] = useState(null);
  const [csvText, setCsvText] = useState("");
  const [report, setReport] = useState(null);
  const [validated, setValidated] = useState(false);
  const [showFormat, setShowFormat] = useState(true);
  const [importCsv, csvState] = useImportUsersCsvMutation();
  useEffect(() => setValidated(false), [csvText]);

  async function pickFile(e) {
    const picked = e.target.files && e.target.files[0];
    e.target.value = "";
    setReport(null);
    if (!picked) return;
    if (picked.size > CSV_MAX_BYTES) {
      setFile(null);
      setCsvText("");
      setReport({ error: "The file is larger than 500 kB." });
      return;
    }
    setFile(picked);
    setCsvText(await picked.text());
  }
  async function sendCsv(dryRun) {
    setReport(null);
    const response = await importCsv({ body: { csv: csvText, dryRun } });
    setShowFormat(false);
    if (response.error) {
      const body = response.error.data || {};
      setValidated(false);
      setReport({ error: body.message || "The file could not be checked", formatErrors: body.payload?.errors, rows: body.payload?.rows });
      return;
    }
    const body = response.data;
    setReport({ message: body.message, created: dryRun ? null : body.payload?.created });
    setValidated(dryRun);
    if (!dryRun) {
      setFile(null);
      setCsvText("");
    }
  }
  function downloadTemplate() {
    const link = document.createElement("a");
    link.href = URL.createObjectURL(new Blob([CSV_TEMPLATE], { type: "text/csv;charset=utf-8" }));
    link.download = "User import template.csv";
    link.click();
    setTimeout(() => URL.revokeObjectURL(link.href), 1000);
  }

  const smallTable = (head, body) => (
    <TableContainer style={{ flex: 1 }}>
      <Table size="small" stickyHeader>
        <TableHead>
          <TableRow>
            {head.map((h) => (
              <TableCell key={h}>{h}</TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>{body}</TableBody>
      </Table>
    </TableContainer>
  );

  return (
    <Dialog open onClose={csvState.isLoading ? undefined : onClose} maxWidth="lg" fullWidth>
      <DialogTitle>Upload Users (CSV)</DialogTitle>
      <DialogContent style={{ height: "65vh", display: "flex", flexDirection: "column", gap: "10px", paddingTop: "5px" }}>
        <div style={{ display: "flex", gap: "10px", flexWrap: "wrap", alignItems: "center", paddingTop: "5px" }}>
          <Button variant="outlined" component="label">
            {file ? file.name : "Choose CSV file"}
            <input id="import-users-file" type="file" accept=".csv,text/csv" hidden onChange={pickFile} />
          </Button>
          <Button onClick={downloadTemplate}>Download template</Button>
          <Button id="import-users-format-toggle" onClick={() => setShowFormat((v) => !v)}>
            {showFormat ? "Hide file format" : "Show file format"}
          </Button>
        </div>
        {showFormat ? (
          <div id="import-users-format">
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Column</TableCell>
                  <TableCell>Needed</TableCell>
                  <TableCell>What goes in it</TableCell>
                  <TableCell>Example</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {csvGuide(tfaAllowed).map(([column, needed, what, example]) => (
                  <TableRow key={column}>
                    <TableCell style={{ whiteSpace: "nowrap" }}>
                      <b>{column}</b>
                    </TableCell>
                    <TableCell style={{ whiteSpace: "nowrap" }}>{needed}</TableCell>
                    <TableCell>{what}</TableCell>
                    <TableCell style={{ whiteSpace: "nowrap", color: "#777" }}>{example}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            <ul style={{ fontSize: "13px", color: "#555", margin: "10px 0 0", paddingLeft: "20px", lineHeight: 1.7 }}>
              <li>The first row names the columns (any order, capitals ignored); then one user per row.</li>
              <li>UTF-8 text (in Excel: Save As "CSV UTF-8"), comma-separated, up to 200 users per file.</li>
              <li>Validate checks every row and your licence first; if anything has a problem, nothing is saved.</li>
              <li>
                Each new user gets the "set your password" e-mail; they go out one at a time, so a large file takes a few
                minutes. Anyone who did not get one can be sent it with Reset Password in the user list.
              </li>
            </ul>
          </div>
        ) : null}
        {csvState.isLoading ? <LinearProgress /> : null}
        {report?.message ? <Alert severity="success">{report.message}</Alert> : null}
        {report?.created?.length
          ? smallTable(
              ["Line", "Username", "E-mail", "Role"],
              report.created.map((u) => (
                <TableRow key={u.line}>
                  <TableCell>{u.line}</TableCell>
                  <TableCell style={{ whiteSpace: "nowrap" }}>{u.userName}</TableCell>
                  <TableCell style={{ whiteSpace: "nowrap" }}>{u.email}</TableCell>
                  <TableCell style={{ whiteSpace: "nowrap" }}>{u.role}</TableCell>
                </TableRow>
              ))
            )
          : null}
        {report?.error ? <Alert severity="error">{report.error}</Alert> : null}
        {report?.formatErrors?.length
          ? smallTable(
              ["Line", "Problem"],
              report.formatErrors.map((e, i) => (
                <TableRow key={i}>
                  <TableCell>{e.line || "-"}</TableCell>
                  <TableCell>{e.message}</TableCell>
                </TableRow>
              ))
            )
          : null}
        {report?.rows?.length
          ? smallTable(
              ["Line", "E-mail", "Username", "Role", "Problems"],
              report.rows
                .filter((r) => r.errors.length)
                .map((r) => (
                  <TableRow key={r.line}>
                    <TableCell>{r.line}</TableCell>
                    <TableCell style={{ whiteSpace: "nowrap" }}>{r.email || "-"}</TableCell>
                    <TableCell style={{ whiteSpace: "nowrap" }}>{r.userName || "-"}</TableCell>
                    <TableCell style={{ whiteSpace: "nowrap" }}>{r.role || "-"}</TableCell>
                    <TableCell>{r.errors.join("; ")}</TableCell>
                  </TableRow>
                ))
            )
          : null}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={csvState.isLoading}>
          Close
        </Button>
        <Button id="import-users-validate" onClick={() => sendCsv(true)} disabled={!csvText || csvState.isLoading}>
          Validate
        </Button>
        <Button
          id="import-users-import"
          variant="contained"
          color="secondary"
          onClick={() => sendCsv(false)}
          disabled={!csvText || !validated || csvState.isLoading}
        >
          Import
        </Button>
      </DialogActions>
    </Dialog>
  );
}
